import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Badge,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Event as EventIcon,
  Warning as WarningIcon,
  ChangeCircle as ChangeCircleIcon,
  FolderOpen as FolderOpenIcon,
  CalendarMonth as CalendarMonthIcon,
  Schedule as ScheduleIcon,
  MedicalServices as MedicalServicesIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../hooks/useAuth';
import pacienteService from '../../services/pacienteService';
import especialistaService from '../../services/especialistaService';
import especialidadService from '../../services/especialidadService';
import PageHeader from '../../components/common/PageHeader';
import CambioEstadoPaciente from '../../components/pacientes/CambioEstadoPaciente';
import { debounce } from 'lodash';

const customChipStyles = {
  borderRadius: '16px',
  fontWeight: 'medium',
  padding: '2px 8px',
  fontSize: '0.75rem'
};

const getEstadoColor = (estado) => {
  switch (estado) {
    case 'Activo':
      return 'success';
    case 'Inactivo':
      return 'error';
    case 'De alta':
      return 'info';
    case 'Pausa':
      return 'warning';
    default:
      return 'default';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: es });
  } catch (error) {
    return dateString;
  }
};

const PacientesList = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [estadoFilter, setEstadoFilter] = useState('');
  const [especialistaFilter, setEspecialistaFilter] = useState('');
  const [especialidadFilter, setEspecialidadFilter] = useState('');
  const [especialistas, setEspecialistas] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [cambioEstadoDialogOpen, setCambioEstadoDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const limit = 10;
  
  // Función segura para establecer la página
  const setPageSafe = (newPage) => {
    const pageNum = parseInt(newPage) || 1;
     
    setPage(pageNum);
  };
  
  // Función helper para obtener el rol correctamente
  const getUserRole = () => {
    if (!user) return null;
    
    if (typeof user.rol === 'object' && user.rol !== null) {
      return user.rol.nombre || user.rol.name || user.rol.tipo || null;
    }
    
    if (typeof user.rol === 'string') {
      return user.rol;
    }
    
    return null;
  };
  
  // Verificar permisos para crear/editar
  const canCreate = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };
  
  // Verificar permisos para editar
  const canEdit = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };
  
  // Verificar permisos para cambiar estado - Actualizado para incluir Admisión
  const canChangeStatus = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };
  
  // Crear función debounced para búsqueda
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPageSafe(1);
    }, 300),
    []
  );
  
 
  
  // Cargar especialistas y especialidades para filtros
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [especialistasRes, especialidadesRes] = await Promise.all([
          especialistaService.getEspecialistas(),
          especialidadService.getEspecialidades()
        ]);
        
        setEspecialistas(especialistasRes.data || []);
        setEspecialidades(especialidadesRes.data || []);
      } catch (error) {
        console.error('Error loading filter data:', error);
      }
    };
    
    loadFilterData();
  }, []);
  
  // Cargar pacientes con protección contra bucles infinitos
  useEffect(() => {
    let isMounted = true; // Para evitar actualizaciones en componentes desmontados
    let retryCount = 0;
    const maxRetries = 3;
    
    const fetchPatients = async () => {
      if (!isMounted) return;
      
      setLoading(true);
      try {
        // Asegurar que page es un número
        const currentPage = parseInt(page) || 1;
        const currentLimit = parseInt(limit) || 10;
        
      
        const params = {
          page: currentPage,
          limit: currentLimit,
          search: search || '',
          estado: estadoFilter || '',
          especialista_id: especialistaFilter || '',
          especialidad_id: especialidadFilter || ''
        };
        
        const response = await pacienteService.getPacientes(params);
        
        if (!isMounted) return;
        
        let allPatients = [];
        if (response.data && Array.isArray(response.data.pacientes)) {
          allPatients = response.data.pacientes;
        } else if (response.data && Array.isArray(response.data)) {
          allPatients = response.data;
        }
        
        setPatients(allPatients);
        setTotalPages(response.data.totalPages || Math.ceil(allPatients.length / currentLimit));
        retryCount = 0; // Reset retry count on success
        
      } catch (error) {
        console.error('Error fetching patients:', error);
        
        if (!isMounted) return;
        
        // Si es error 401, no reintentar
        if (error.response?.status === 401 || error.message === 'Sesión expirada') {
          showNotification('Sesión expirada. Por favor, inicie sesión nuevamente.', 'error');
          setPatients([]);
          setTotalPages(1);
          setLoading(false);
          return;
        }
        
        // Para otros errores, reintentar hasta 3 veces
        if (retryCount < maxRetries) {
          retryCount++;
        
          setTimeout(() => {
            if (isMounted) fetchPatients();
          }, 1000 * retryCount); // Espera incremental
        } else {
          showNotification('Error al cargar los pacientes', 'error');
          setPatients([]);
          setTotalPages(1);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchPatients();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [page, search, estadoFilter, especialistaFilter, especialidadFilter, refreshKey, showNotification, limit]);
  
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    debouncedSearch(value);
  };
  
  const handleEstadoChange = (event) => {
    setEstadoFilter(event.target.value);
    setPageSafe(1);
  };
  
  const handleEspecialistaChange = (event) => {
    setEspecialistaFilter(event.target.value);
    setPageSafe(1);
  };
  
  const handleEspecialidadChange = (event) => {
    setEspecialidadFilter(event.target.value);
    setPageSafe(1);
  };
  
  const handlePageChange = (event, value) => {
    
    setPageSafe(value);
  };
  
  const handleMenuOpen = (event, patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleOpenCambioEstadoDialog = () => {
    setCambioEstadoDialogOpen(true);
    handleMenuClose();
  };
  
  const handleCloseCambioEstadoDialog = () => {
    setCambioEstadoDialogOpen(false);
  };
  
  const handleEstadoChanged = (nuevoEstado) => {
    setRefreshKey(old => old + 1);
    showNotification('Estado del paciente actualizado correctamente', 'success');
  };
  
  // Verificar si el paciente tiene citas programadas
  const tieneCitasProgramadas = (patient) => {
    return patient.citas && patient.citas.length > 0;
  };

  const obtenerEspecialistas = (patient) => {
    if (!patient.asignaciones || patient.asignaciones.length === 0) return [];
    return patient.asignaciones.map(asig => ({
      id: asig.especialista?.id,
      nombre: asig.especialista?.usuario?.nombre + ' ' + asig.especialista?.usuario?.apellidos,
      especialidad: asig.especialidad?.nombre
    }));
  };

  // Función para obtener etapa actual
  const obtenerEtapaActual = (patient) => {
    if (patient.HistoriaClinica?.especialidades?.length > 0) {
      return patient.HistoriaClinica.especialidades[0].estado;
    }
    return null;
  };
  
  // Componente para mostrar información en móvil (Card)
  const PatientCard = ({ patient }) => (
    <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
              {patient.nombres} {patient.apellidos}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              HC: {patient.numero_historia} | DNI: {patient.dni || '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {patient.contador_inasistencias > 2 && (
              <Badge 
                badgeContent={patient.contador_inasistencias} 
                color="error"
                sx={{ mr: 1 }}
              >
                <WarningIcon color="error" />
              </Badge>
            )}
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, patient)}
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
        </Box>
        
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Edad</Typography>
            <Typography variant="body2">{patient.edad || '-'} años</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Registro</Typography>
            <Typography variant="body2">{formatDate(patient.fecha_registro)}</Typography>
          </Grid>
        </Grid>
        
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={patient.estado}
            color={getEstadoColor(patient.estado)}
            size="small"
            sx={{
              ...customChipStyles, 
              fontWeight: 'bold' 
            }}
          />
          {obtenerEtapaActual(patient) && (
            <Chip
              label={obtenerEtapaActual(patient)}
              size="small"
              variant="outlined"
              icon={<PsychologyIcon />}
            />
          )}
          <Chip
            label={tieneCitasProgramadas(patient) ? "Programado" : "Sin programación"}
            color={tieneCitasProgramadas(patient) ? "primary" : "default"}
            size="small"
            icon={<ScheduleIcon />}
            variant={tieneCitasProgramadas(patient) ? "filled" : "outlined"}
          />
        </Stack>
        
        {obtenerEspecialistas(patient).length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <MedicalServicesIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
              Especialistas:
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {obtenerEspecialistas(patient).map(esp => `${esp.nombre} (${esp.especialidad})`).join(', ')}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            component={RouterLink}
            to={`/pacientes/${patient.id}`}
            size="small"
            startIcon={<VisibilityIcon />}
          >
            Ver
          </Button>
          {canEdit() && (
            <Button
              component={RouterLink}
              to={`/pacientes/editar/${patient.id}`}
              size="small"
              color="warning"
              startIcon={<EditIcon />}
              sx={{ ml: 1 }}
            >
              Editar
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader 
        title="Pacientes"
        subtitle="Gestión de pacientes del centro"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Pacientes' }
        ]}
        actionButton={
          canCreate() && (
            <Button
              component={RouterLink}
              to="/pacientes/nuevo"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              size={isMobile ? "small" : "medium"}
            >
              {isMobile ? "Nuevo" : "Nuevo Paciente"}
            </Button>
          )
        }
      />
      
      {/* Filtros */}
      <Card sx={{ mb: 3 }} elevation={2}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Buscar por nombre, apellido, DNI o número de historia..."
                value={searchInput}
                onChange={handleSearchChange}
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" size={isMobile ? "small" : "medium"}>
                <InputLabel id="estado-select-label">Estado</InputLabel>
                <Select
                  labelId="estado-select-label"
                  value={estadoFilter}
                  label="Estado"
                  onChange={handleEstadoChange}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Activo">Activo</MenuItem>
                  <MenuItem value="Inactivo">Inactivo</MenuItem>
                  <MenuItem value="De alta">De alta</MenuItem>
                  <MenuItem value="Pausa">En pausa</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" size={isMobile ? "small" : "medium"}>
                <InputLabel id="especialista-select-label">Especialista</InputLabel>
                <Select
                  labelId="especialista-select-label"
                  value={especialistaFilter}
                  label="Especialista"
                  onChange={handleEspecialistaChange}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {especialistas.map(esp => (
                    <MenuItem key={esp.id} value={esp.id}>
                      {esp.usuario?.nombre || esp.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" size={isMobile ? "small" : "medium"}>
                <InputLabel id="especialidad-select-label">Especialidad</InputLabel>
                <Select
                  labelId="especialidad-select-label"
                  value={especialidadFilter}
                  label="Especialidad"
                  onChange={handleEspecialidadChange}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {especialidades.map(esp => (
                    <MenuItem key={esp.id} value={esp.id}>
                      {esp.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Contenido principal */}
      <Card elevation={2}>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : patients.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body1" color="textSecondary" gutterBottom>
                No se encontraron pacientes con los filtros aplicados.
              </Typography>
              {canCreate() && (
                <Button
                  component={RouterLink}
                  to="/pacientes/nuevo"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                >
                  Nuevo Paciente
                </Button>
              )}
            </Box>
          ) : (
            <>
              {/* Vista móvil - Cards */}
              {isMobile ? (
                <Box>
                  {patients.map((patient) => (
                    <PatientCard key={patient.id} patient={patient} />
                  ))}
                </Box>
              ) : (
                /* Vista desktop - Tabla */
                <TableContainer component={Paper} elevation={0}>
                  <Table size={isTablet ? "small" : "medium"}>
                    <TableHead>
                      <TableRow>
                        <TableCell>N° Historia</TableCell>
                        <TableCell>Paciente</TableCell>
                        {!isTablet && <TableCell>DNI</TableCell>}
                        {!isTablet && <TableCell>Edad</TableCell>}
                        <TableCell>Estado</TableCell>
                        <TableCell>Programación</TableCell>
                        <TableCell>Especialistas</TableCell>
                        {!isTablet && <TableCell align="center">Inasistencias</TableCell>}
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patients.map((patient) => (
                        <TableRow 
                          key={patient.id}
                          sx={{
                            backgroundColor: patient.contador_inasistencias > 2 ? 'rgba(255, 152, 0, 0.1)' : 'inherit',
                            '&:hover': {
                              backgroundColor: patient.contador_inasistencias > 2 ? 'rgba(255, 152, 0, 0.2)' : 'rgba(0, 0, 0, 0.04)',
                            }
                          }}
                        >
                          <TableCell>{patient.numero_historia}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {patient.nombres} {patient.apellidos}
                              </Typography>
                              {isTablet && (
                                <Typography variant="caption" color="text.secondary">
                                  DNI: {patient.dni || '-'} | {patient.edad || '-'} años
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          {!isTablet && <TableCell>{patient.dni || '-'}</TableCell>}
                          {!isTablet && <TableCell>{patient.edad || '-'}</TableCell>}
                          <TableCell>
                            <Box>
                              <Chip
                                label={patient.estado}
                                color={getEstadoColor(patient.estado)}
                                size="small"
                                sx={{ 
                                  ...customChipStyles,
                                  fontWeight: 'bold' 
                                }}
                              />
                              {obtenerEtapaActual(patient) && (
                                <Chip
                                    label={obtenerEtapaActual(patient)}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    sx={{ 
                                      ...customChipStyles,
                                      mt: 0.5,
                                      display: 'block',
                                      width: 'fit-content'
                                    }}
                                  />
                                )}                            
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={tieneCitasProgramadas(patient) ? "Programado" : "Sin programación"}
                              color={tieneCitasProgramadas(patient) ? "primary" : "default"}
                              size="small"
                              variant={tieneCitasProgramadas(patient) ? "filled" : "outlined"}
                              icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                ...customChipStyles,
                                '& .MuiChip-icon': {
                                  marginLeft: '4px',
                                  marginRight: '-4px'
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {obtenerEspecialistas(patient).length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {obtenerEspecialistas(patient).slice(0, 2).map((esp, index) => (
                                  <Chip
                                    key={index}
                                    label={`${esp.nombre} - ${esp.especialidad}`}
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    sx={{
                                      ...customChipStyles,
                                      maxWidth: '200px',
                                      '& .MuiChip-label': {
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                      }
                                    }}
                                  />
                                ))}
                                {obtenerEspecialistas(patient).length > 2 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    +{obtenerEspecialistas(patient).length - 2} más
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Chip
                                label="Sin asignar"
                                size="small"
                                variant="outlined"
                                sx={{
                                  ...customChipStyles,
                                  color: 'text.disabled',
                                  borderColor: 'divider'
                                }}
                              />
                            )}
                          </TableCell>
                          {!isTablet && (
                            <TableCell align="center">
                              {patient.contador_inasistencias > 0 ? (
                                <Chip
                                  label={patient.contador_inasistencias}
                                  color={patient.contador_inasistencias > 2 ? "error" : "warning"}
                                  size="small"
                                  icon={patient.contador_inasistencias > 2 ? <WarningIcon /> : null}
                                  sx={{
                                    ...customChipStyles,
                                    fontWeight: 'bold'
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" color="textSecondary">-</Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Tooltip title="Ver detalles">
                                <IconButton
                                  component={RouterLink}
                                  to={`/pacientes/${patient.id}`}
                                  size="small"
                                  color="info"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              
                              {canEdit() && (
                                <Tooltip title="Editar">
                                  <IconButton
                                    component={RouterLink}
                                    to={`/pacientes/editar/${patient.id}`}
                                    size="small"
                                    color="warning"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              <Tooltip title="Más acciones">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuOpen(e, patient)}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
          
          {/* Paginación */}
          {!loading && patients.length > 0 && (
            <Box p={2} display="flex" justifyContent="center">
              <Pagination
                count={parseInt(totalPages) || 1}
                page={parseInt(page) || 1}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
              />
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Menú de acciones adicionales */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {/* Ver HCE - Todos pueden ver */}
        <MenuItem 
          component={RouterLink} 
          to={selectedPatient ? `/historias?paciente=${selectedPatient.id}` : '#'}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <FolderOpenIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary="Ver HCE" />
        </MenuItem>
        
        <Divider />
        
        {/* Agendar Cita - Solo Admin y Admisión */}
        {canCreate() && (
          <MenuItem 
            component={RouterLink} 
            to={selectedPatient ? `/citas/nueva?paciente=${selectedPatient.id}` : '#'}
            onClick={handleMenuClose}
          >
            <ListItemIcon>
              <EventIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText primary="Agendar Cita" />
          </MenuItem>
        )}
        
        {/* Cambiar estado - Admin y Admisión */}
        {canChangeStatus() && (
          <MenuItem onClick={handleOpenCambioEstadoDialog}>
            <ListItemIcon>
              <ChangeCircleIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText primary="Cambiar Estado" />
          </MenuItem>
        )}
      </Menu>
      
      {/* Modal de cambio de estado */}
      {selectedPatient && (
        <CambioEstadoPaciente
          open={cambioEstadoDialogOpen}
          onClose={handleCloseCambioEstadoDialog}
          paciente={selectedPatient}
          onEstadoChanged={handleEstadoChanged}
        />
      )}
    </Box>
  );
};

export default PacientesList;